import { useState, useEffect } from "react";
import { apiFetch } from "../../api/api";
import { T } from "../data/Tokens";
import {
  Card, PageHeader, GoldBtn, GhostBtn, StatusPill,
  SearchBar, Avatar, Th, Td, Tr, Tabs, StatCard, EmptyState, Textarea
} from "../components/common/Ui";

const Stars = ({ n }) => (
  <div className="flex gap-0.5">
    {[1,2,3,4,5].map(i => (
      <svg key={i} viewBox="0 0 24 24" className="w-3.5 h-3.5"
        fill={i <= n ? "#C8A46A" : "none"} stroke="#C8A46A" strokeWidth="1.5">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ))}
  </div>
);

export default function ReviewsAdmin() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [meta, setMeta] = useState({ avgRating: 0, withPhotos: 0 });

  const [tab, setTab]         = useState("all");
  const [search, setSearch]   = useState("");
  const [reply, setReply]     = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [toast, setToast]     = useState(null);

  // ── Fetch reviews from backend ──────────────────────────────────────────────
  const fetchReviews = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await apiFetch("/reviews/list.php");
      setReviews(res.data || []);
      setMeta({ avgRating: res.avgRating || 0, withPhotos: res.withPhotos || 0 });
    } catch (err) {
      setLoadError(err.message || "Failed to load reviews.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReviews(); }, []);

  const showToast = (msg, color) => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 2500);
  };

  // ── Status update (backend connected) ───────────────────────────────────────
  const updateStatus = async (dbId, status) => {
    setUpdatingId(dbId);
    try {
      await apiFetch("/reviews/update.php", {
        method: "PUT",
        body: { id: dbId, status },
      });
      setReviews(prev => prev.map(r => r.dbId === dbId ? { ...r, status } : r));
      showToast(status === "Approved" ? "✓ Review Approved" : "✗ Review Rejected", status === "Approved" ? T.success : T.danger);
    } catch (err) {
      showToast(err.message || "Failed to update review", T.danger);
    } finally {
      setUpdatingId(null);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      await apiFetch("/reviews/update.php", {
        method: "PUT",
        body: { id: reply.dbId, adminReply: replyText.trim() },
      });
      setReviews(prev => prev.map(r => r.dbId === reply.dbId ? { ...r, adminReply: replyText.trim() } : r));
      showToast("💬 Reply sent successfully", T.gold);
      setReply(null);
      setReplyText("");
    } catch (err) {
      showToast(err.message || "Failed to send reply", T.danger);
    } finally {
      setSendingReply(false);
    }
  };

  // ── Stats (live from state) ───────────────────────────────────────────────
  const approvedCount = reviews.filter(r=>r.status==="Approved").length;
  const STATS = [
    { icon:"⭐", label:"Total Reviews", value: reviews.length, sub:"All time", color: T.gold },
    { icon:"✅", label:"Approved",      value: approvedCount, sub: reviews.length ? `${Math.round((approvedCount/reviews.length)*100)}%` : "0%", color: T.success },
    { icon:"⏳", label:"Pending",       value: reviews.filter(r=>r.status==="Pending").length,    sub:"Awaiting review", color: T.warning, bg:"#FFF8EC" },
    { icon:"❌", label:"Rejected",      value: reviews.filter(r=>r.status==="Rejected").length,   sub:"Hidden",          color: T.danger,  bg:"#FFF0F0" },
    { icon:"🌟", label:"Avg Rating",    value: meta.avgRating || (reviews.length ? (reviews.reduce((a,r)=>a+r.rating,0)/reviews.length).toFixed(1) : "0.0"), sub:"Out of 5", color: T.gold },
    { icon:"📸", label:"With Photos",   value: meta.withPhotos, sub:"", color:"#5B7EE5",  bg:"#EEF3FF" },
  ];

  const TABS = [
    { id:"all",      label:"All",      count: reviews.length },
    { id:"pending",  label:"Pending",  count: reviews.filter(r=>r.status==="Pending").length },
    { id:"approved", label:"Approved", count: reviews.filter(r=>r.status==="Approved").length },
    { id:"rejected", label:"Rejected", count: reviews.filter(r=>r.status==="Rejected").length },
  ];

  const filtered = reviews.filter(r => {
    const ms = !search || r.customer.toLowerCase().includes(search.toLowerCase()) || r.product.toLowerCase().includes(search.toLowerCase());
    const mt = tab === "all" || r.status.toLowerCase() === tab;
    return ms && mt;
  });

  return (
    <div>
      <PageHeader title="Reviews" subtitle={`${reviews.length} total reviews`}>
        <GhostBtn>↓ Export</GhostBtn>
      </PageHeader>

      {/* Loading / Error states */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full animate-spin"
              style={{ border: `3px solid ${T.border}`, borderTopColor: T.gold }} />
            <p className="text-[12px]" style={{ color: T.textSec }}>Loading reviews...</p>
          </div>
        </div>
      )}

      {!loading && loadError && (
        <Card className="p-6 text-center mb-5">
          <p className="text-[13px] font-semibold mb-2" style={{ color: T.danger }}>{loadError}</p>
          <button onClick={fetchReviews}
            className="px-4 py-2 rounded-xl text-[12px] font-semibold text-white"
            style={{ background: T.gold }}>
            Retry
          </button>
        </Card>
      )}

      {!loading && !loadError && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5 sm:gap-3 mb-5 sm:mb-6">
            {STATS.map(s => <StatCard key={s.label} {...s} />)}
          </div>

          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-1">
            <Tabs tabs={TABS} active={tab} onChange={setTab} />
          </div>

          <Card>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-3 px-4 sm:px-5 py-3.5 sm:py-4" style={{ borderBottom:`1px solid ${T.border}` }}>
              <SearchBar value={search} onChange={setSearch} placeholder="Search by customer or product..." />
              <select className="w-full sm:w-auto text-[12px] px-3 py-2.5 rounded-xl outline-none flex-shrink-0"
                style={{ border:`1px solid ${T.border}`, background:"#fff", color:T.textSec }}>
                <option>All Ratings</option><option>5 Stars</option><option>4 Stars</option>
                <option>3 Stars</option><option>2 Stars</option><option>1 Star</option>
              </select>
            </div>

            {filtered.length === 0
              ? <EmptyState icon="⭐" title="No reviews found" sub="Try changing filters." />
              : (
                <div className="divide-y" style={{ borderColor: T.bgSoft }}>
                  {filtered.map(r => (
                    <div key={r.dbId} className="px-4 sm:px-5 py-4 transition-colors hover:bg-amber-50/30">
                      <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-3 sm:gap-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <Avatar name={r.customer} size={36} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                              <p className="text-[13px] font-semibold" style={{ color: T.textPri }}>{r.customer}</p>
                              <Stars n={r.rating} />
                              <span className="text-[10px]" style={{ color: T.textSec }}>{r.date}</span>
                              <StatusPill s={r.status} />
                            </div>
                            <p className="text-[11px] mt-0.5" style={{ color: T.gold }}>on {r.product}</p>
                            <p className="text-[12px] mt-2 leading-relaxed" style={{ color: T.textSec }}>{r.comment}</p>
                            {r.adminReply && (
                              <div className="mt-2 rounded-xl px-3 py-2" style={{ background: T.bgSoft }}>
                                <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: T.gold }}>Your Reply</p>
                                <p className="text-[11.5px] leading-relaxed" style={{ color: T.textSec }}>{r.adminReply}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Action buttons — mobile: full-width horizontal row below the review; desktop: stacked column on the right */}
                        <div className="flex flex-row sm:flex-col gap-1.5 flex-shrink-0 w-full sm:w-auto">
                          {r.status === "Pending" && (
                            <>
                              <button
                                onClick={() => updateStatus(r.dbId, "Approved")}
                                disabled={updatingId === r.dbId}
                                className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white transition-all hover:opacity-90 active:scale-95 disabled:opacity-60"
                                style={{ background: T.success }}>
                                ✓ Approve
                              </button>
                              <button
                                onClick={() => updateStatus(r.dbId, "Rejected")}
                                disabled={updatingId === r.dbId}
                                className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white transition-all hover:opacity-90 active:scale-95 disabled:opacity-60"
                                style={{ background: T.danger }}>
                                ✗ Reject
                              </button>
                            </>
                          )}

                          {/* Already approved — show reject option */}
                          {r.status === "Approved" && (
                            <button
                              onClick={() => updateStatus(r.dbId, "Rejected")}
                              disabled={updatingId === r.dbId}
                              className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-all hover:bg-red-50 active:scale-95 disabled:opacity-60"
                              style={{ borderColor: T.danger, color: T.danger }}>
                              ✗ Reject
                            </button>
                          )}

                          {/* Already rejected — show approve option */}
                          {r.status === "Rejected" && (
                            <button
                              onClick={() => updateStatus(r.dbId, "Approved")}
                              disabled={updatingId === r.dbId}
                              className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-all hover:bg-green-50 active:scale-95 disabled:opacity-60"
                              style={{ borderColor: T.success, color: T.success }}>
                              ✓ Approve
                            </button>
                          )}

                          <button
                            onClick={() => { setReply(r); setReplyText(r.adminReply || ""); }}
                            className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg text-[11px] font-medium border hover:bg-amber-50 transition-all active:scale-95"
                            style={{ borderColor: T.border, color: T.textSec }}>
                            💬 Reply
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </Card>
        </>
      )}

      {/* Reply Modal */}
      {reply && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background:"rgba(0,0,0,0.5)" }}>
          <div className="w-full max-w-md rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto" style={{ background: T.card }}>
            <div className="flex items-center justify-between px-5 sm:px-6 py-3.5 sm:py-4" style={{ borderBottom:`1px solid ${T.border}` }}>
              <h2 className="text-[15px] sm:text-[16px] font-bold" style={{ color: T.textPri, fontFamily: T.serif }}>Reply to Review</h2>
              <button onClick={() => setReply(null)} className="text-gray-400 hover:text-gray-700 text-xl">×</button>
            </div>
            <div className="p-5 sm:p-6 space-y-4">
              {/* Original review */}
              <div className="rounded-2xl p-4" style={{ background: T.bgSoft }}>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <p className="text-[12px] font-semibold" style={{ color: T.textPri }}>{reply.customer}</p>
                  <Stars n={reply.rating} />
                  <StatusPill s={reply.status} />
                </div>
                <p className="text-[11px]" style={{ color: T.gold }}>on {reply.product}</p>
                <p className="text-[12px] mt-1.5 leading-relaxed" style={{ color: T.textSec }}>{reply.comment}</p>
              </div>

              {/* Reply box */}
              <div>
                <label className="block text-[11px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color: T.textSec }}>
                  Your Reply
                </label>
                <textarea
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  placeholder="Write your reply to this customer..."
                  rows={4}
                  className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none resize-none transition-all"
                  style={{ background: T.bgSoft, border:`1px solid ${T.border}`, color: T.textPri }}
                  onFocus={e => e.target.style.borderColor = T.gold}
                  onBlur={e => e.target.style.borderColor = T.border}
                />
                {replyText.trim() === "" && (
                  <p className="text-[10px] mt-1" style={{ color: T.danger }}>Reply cannot be empty</p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={sendReply}
                  disabled={!replyText.trim() || sendingReply}
                  className="flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-[13px] font-semibold text-white transition-all disabled:opacity-60"
                  style={{ background: replyText.trim() ? T.gold : T.border, cursor: replyText.trim() ? "pointer" : "not-allowed" }}>
                  {sendingReply ? "Sending..." : "📤 Send Reply"}
                </button>
                <button
                  onClick={() => { if (!sendingReply) { setReply(null); setReplyText(""); } }}
                  disabled={sendingReply}
                  className="px-5 py-2.5 rounded-xl text-[13px] font-medium border transition-all hover:bg-gray-50 disabled:opacity-50"
                  style={{ borderColor: T.border, color: T.textSec }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-[100] flex items-center justify-center sm:justify-start gap-3 px-5 py-3 rounded-2xl text-white text-[13px] font-semibold shadow-2xl transition-all"
          style={{ background: toast.color }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}